
import { SelectionMode } from "../wgs/scene/SelectionMode";
import { SelectionType } from './SelectionType';
import { errorCodeString, ErrorCodes } from "../file-loaders/net/ErrorCodes";
import { logger } from "../logger/Logger";
import * as et from "../application/EventTypes";
import THREE from "three";

/**
 * The selector class doesn't fire any events.
 * All events get fired by the MultiSelector class, instead.
 *
 * @param {*} viewer
 * @param {*} model
 */
export class Selector {
  //Selection support
  selectedObjectIds = {}; // Stores selectionType per dbId (or 0 if not selected)
  selectionCount = 0;
  selectionMode = SelectionMode.LEAF_OBJECT;
  defaultSelectionType = SelectionType.MIXED;
  lockedNodes = []; // Keep track of the locked dbIds

  _selectedParentMap = {};

  constructor(viewer, model) {
    this._viewer = viewer;
    this._model = model;
  }

  getInstanceTree() {
    return this._model.getData().instanceTree;
  }

  _unmarkObject(dbId) {

    var it = this.getInstanceTree();

    if (this._selectedParentMap[dbId] > 0) {
      this._selectedParentMap[dbId]--;
      if (this._selectedParentMap[dbId] == 0) {
        this._viewer.highlightObjectNode(this._model, dbId, false);
      }

    } else if (this._selectedParentMap[dbId] < 0) {
      throw "Selection State machine broken. Negatively selected object!";
    }

    if (it) {
      it.enumNodeChildren(dbId, (childId) => {
        this._unmarkObject(childId);
      }, false);
    }
  }

  _markObject(dbId, isChild, selectionType) {

    var it = this.getInstanceTree();

    if (this._selectedParentMap[dbId]) {
      this._selectedParentMap[dbId]++;
    } else {
      switch (selectionType) {
        default:
        case SelectionType.MIXED:
          this._viewer.highlightObjectNode(this._model, dbId, true, isChild);
          break;
        case SelectionType.REGULAR:
          this._viewer.highlightObjectNode(this._model, dbId, true, true);
          break;
        case SelectionType.OVERLAYED:
          this._viewer.highlightObjectNode(this._model, dbId, true, false);
          break;
      }
      this._selectedParentMap[dbId] = 1;
    }

    if (it) {
      it.enumNodeChildren(dbId, (childId) => {
        this._markObject(childId, true, selectionType);
      }, false);
    }
  }

  _isSelected(dbId) {

    if (dbId !== undefined && this.selectedObjectIds[dbId])
    return true;
  }

  _getSelectionMode() {
    const isIfc = this._model.getData()?.loadOptions?.fileExt === 'ifc';
    const selectionModeChosen = this._viewer.api.prefs.getPrefFromLocalStorage(Autodesk.Viewing.Private.Prefs3D.SELECTION_MODE) !== undefined;

    // https://jira.autodesk.com/browse/BLMV-6235
    //
    // Following some research and experimentation with IFC object hierarchy and properties,
    // the recommendation is to change the default selection mode setting for IFC files only to "First Object".
    // If the user changed the default settings in the settings panel - the selected setting should be persisted in browser cache and applied to all file types (both IFC and non-IFC).
    if (isIfc && !selectionModeChosen) {
      return Autodesk.Viewing.SelectionMode.FIRST_OBJECT;
    } else {
      return this.selectionMode;
    }
  }

  _select(dbId, selectionType, mode) {
    // Make sure that the node selection is not turned on.
    const isLocked = this.isNodeSelectionLocked(dbId);
    if (isLocked) return;
    var it = this.getInstanceTree();
    selectionType = selectionType || this.defaultSelectionType;

    if (it) {
      // Note that 0 (LEAF_OBJECT) is a valid selection mode too - so we must distinguish between 0 and undefined.
      const selectionMode = mode ?? this._getSelectionMode();
      dbId = it.findNodeForSelection(dbId, selectionMode);
      if (!it.isNodeSelectable(dbId))
      return;
    }

    var found = this._isSelected(dbId);
    if (!found) {
      this.selectedObjectIds[dbId] = selectionType;
      this.selectionCount++;
      this._markObject(dbId, false, selectionType);
    }
  }

  _deselect(dbId) {

    var found = this._isSelected(dbId);
    if (found) {
      this._unmarkObject(dbId);
      this.selectedObjectIds[dbId] = 0;
      this.selectionCount--;
    }
  }

  _selectionIsEqual(dbNodeArray) {
    if (this.selectionCount !== dbNodeArray.length)
    return false;

    for (var i = 0; i < dbNodeArray.length; i++) {
      if (!this._isSelected(dbNodeArray[i]))
      return false;
    }
    return true;
  }

  // @returns {boolean} true when a part of the model is selected
  hasSelection() {
    return this.selectionCount > 0;
  }

  getSelectionLength() {
    return this.selectionCount;
  }

  getSelection() {
    var ret = [];
    var sset = this.selectedObjectIds;
    for (var p in sset) {
      if (sset[p]) {
        var dbId = parseInt(p);
        ret.push(dbId);
      }
    }

    return ret;
  }

  isSelected(dbId) {
    return this._isSelected(dbId);
  }

  clearNodeSelection(dbId) {
    if (dbId === undefined || this.selectionCount === 0) return false;
    this._unmarkObject(dbId);
    delete this.selectedObjectIds[dbId];
    return true;
  }

  clearSelection(nofire) {
    if (this.selectionCount > 0) {
      var sset = this.selectedObjectIds;
      for (var p in sset) {
        var dbId = parseInt(p);
        if (dbId !== undefined)
        this._unmarkObject(dbId);
      }
      this.selectedObjectIds = {};
      this.selectionCount = 0;
      return true;
    }
  }

  deselectInvisible() {
    var changed = false;

    var sset = this.selectedObjectIds;
    var visMan = this._viewer.visibilityManager;
    for (var p in sset) {
      var dbId = parseInt(p);
      if (dbId && !visMan.isNodeVisible(this._model, dbId)) {
        this._deselect(dbId);
        changed = true;
      }
    }

    return changed;
  }

  // TODO: Optimize this so both select and toggleSelection don't have to lookup the node index.
  toggleSelection(dbId, selectionType) {

    // Notice that for Leaflets, it's ok to select dbId 0 - that's the only single id.
    if (!dbId && !this._model.isLeaflet()) {
      logger.error("Attempting to select node 0.", errorCodeString(ErrorCodes.VIEWER_INTERNAL_ERROR));
      return;
    }

    if (!this._isSelected(dbId)) {
      this._select(dbId, selectionType);
    } else {
      this._deselect(dbId);
    }
  }

  setSelectionMode(mode) {
    this.clearSelection(true);
    this.selectionMode = mode;
  }

  setDefaultSelectionType(type) {
    this.defaultSelectionType = type;
  }

  isNodeSelectionLocked(dbId) {
    if (dbId === -1) return false;
    var instanceTree = this.getInstanceTree();
    if (instanceTree) {
      return instanceTree.isNodeSelectionLocked(dbId);
    } else {
      // Hypothetically, the logic for selection locking can be removed from InstanceTree.js
      // This approach can be used for models that do contain an instance tree.
      return this.lockedNodes.indexOf(dbId) !== -1;
    }

  }

  lockSelection(nodeList, locked) {
    var instanceTree = this.getInstanceTree();
    nodeList = Array.isArray(nodeList) ? nodeList : [nodeList];

    const updateLock = (dbId, locked) => {
      if (!dbId || dbId === -1) return;
      if (locked) {
        if (this.lockedNodes.indexOf(dbId) === -1) {
          this.lockedNodes.push(dbId);
          this.clearNodeSelection(dbId);
        }
      } else {
        const index = this.lockedNodes.indexOf(dbId);
        this.lockedNodes.splice(index, 1);
      }
    };

    if (instanceTree) {
      nodeList.forEach((node) => {
        instanceTree.enumNodeChildren(
          node,
          (dbId) => {
            instanceTree.lockNodeSelection(dbId, false);
            updateLock(dbId, locked);
            if (locked) {
              instanceTree.lockNodeSelection(dbId, locked);
            }
          },
          true
        );
      });
    } else {
      // For models that do not contain an instance tree.
      nodeList.forEach((dbId) => {
        updateLock(dbId, locked);
      });
    }
  }

  setSelection(dbNodeArray, selectionType) {
    if (this._selectionIsEqual(dbNodeArray))
    return;

    this.clearSelection(true);

    if (dbNodeArray == null || dbNodeArray.length === 0)
    return;

    const mode = this._getSelectionMode();
    for (var i = 0; i < dbNodeArray.length; i++) {
      this._select(dbNodeArray[i], selectionType, mode);
    }
  }

  getSelectionBounds() {
    var bounds = new THREE.Box3();
    var box = new THREE.Box3();

    var instanceTree = this.getInstanceTree();
    if (instanceTree) {
      var fragList = this._model.getFragmentList();

      var sset = this.selectedObjectIds;
      for (var p in sset) {
        var dbId = parseInt(p);
        instanceTree.enumNodeFragments(dbId, (fragId) => {
          fragList.getWorldBounds(fragId, box);
          bounds.union(box);
        }, true);
      }
    }

    return bounds;
  }

  getSelectionVisibility() {
    var hasVisible = false,
      hasHidden = false;

    var sset = this.selectedObjectIds;
    for (var p in sset) {
      var dbId = parseInt(p);
      if (dbId) {
        var instanceTree = this.getInstanceTree();
        if (!instanceTree || !instanceTree.isNodeHidden(dbId)) {
          hasVisible = true;
        } else {
          hasHidden = true;
        }
        if (hasVisible && hasHidden) {
          break;
        }
      }
    }

    return {
      hasVisible: hasVisible,
      hasHidden: hasHidden,
      model: this._model
    };
  }

  // If a selection is made right after adding a model, selection highlight might not work
  // yet, because...
  //  a) SelectionTexture may not be initialized yet
  //  b) idRemap may not loaded yet.
  // This function makes sure that the corresponding highlighting is applied as soon as all
  // required data are available.
  update2DSelectionHighlighting() {
    // See comment about updated2DSelectionHighlighting below.
    if (this._2DSelectionHighlightingUpdated) {
      return;
    }

    // SelectionTexture does only exist if the full model geometry is loaded.
    // This is because selectionTexture size depends on maxObjectNumber - which is dynamically tracked during loading.
    if (!this._model.isLoadDone()) {
      return;
    }

    // F2D models with OTG databases require a dbId remapping. The remapping is only available if the instance tree is loaded.
    // Note that it's okay if there is no remapping in some cases. The only problem is if there is one, but just not loaded yet.
    var pdbLoader = this._model.getPropertyDb();
    if (pdbLoader && !pdbLoader.isLoadDone()) {
      return;
    }

    // Make sure the 2d selection overlay is initialized
    if (!this._viewer.overlayScenes[this._viewer.selection2dOverlayName(this._model)])
    return;

    // We will mark all selected objects again. So, we first have to unmark the nodes which are already highlighted.
    // Otherwise, the loop below would not trigger any highlighting update.
    var sel = this.getSelection();
    for (let i = 0; i < sel.length; i++) {
      let dbId = sel[i];
      this._unmarkObject(dbId);
    }

    // Update this.selectedObjectIds keys with new dbIds. Otherwise, the values that getSelection returns will be incorrect.
    const tempMap = {};
    Object.keys(this.selectedObjectIds).forEach((dbId) => {
      tempMap[this._model.remapDbId(dbId)] = this.selectedObjectIds[dbId];
    });
    this.selectedObjectIds = tempMap;

    // Trigger highlighting for all selected objects
    for (let i = 0; i < sel.length; i++) {
      let dbId = sel[i];
      var selectionType = this.selectedObjectIds[dbId];
      this._markObject(dbId, false, selectionType);
    }

    // Make sure that this function was fully executed only once.
    // Basically it shouldn't happen, but in case that this function was somehow being called again
    // after it already transformed the selected objectIds, they might get remapped multiple times and
    // get incorrect values as a result.
    this._2DSelectionHighlightingUpdated = true;
  }

  dtor() {
    this.selectedObjectIds = null;
  }

}

export class MultiModelSelector {
  _models = [];

  highlightDisabled = false;
  highlightPaused = false;
  selectionDisabled = false;

  constructor(viewer) {
    this._viewer = viewer;

    viewer.api.addEventListener(et.ISOLATE_EVENT, (event) => {
      this._deselectInvisible();
    });

    viewer.api.addEventListener(et.HIDE_EVENT, (event) => {
      this._deselectInvisible();
    });

    // Make sure that selection highlighting is updated when all required data are ready.
    const update2DSelectionHighlighting = (e) => {
      // Selector may be null if model has been removed meanwhile
      if (e.model.is2d() && e.model.selector) {
        e.model.selector.update2DSelectionHighlighting();
      }
    };
    viewer.api.addEventListener(et.GEOMETRY_LOADED_EVENT, update2DSelectionHighlighting);
    viewer.api.addEventListener(et.OBJECT_TREE_CREATED_EVENT, update2DSelectionHighlighting);
    viewer.api.addEventListener(et.OBJECT_TREE_UNAVAILABLE_EVENT, update2DSelectionHighlighting);
  }

  addModel(model) {
    if (this._models.indexOf(model) == -1) {
      model.selector = new Selector(this._viewer, model);
      this._models.push(model);
    }
  }

  removeModel(model) {
    var idx = this._models.indexOf(model);

    // make sure that we don't keep any highlighting proxy
    model.selector.clearSelection();
    model.selector = null;
    this._models.splice(idx, 1);
  }

  _warn() {
    if (this._models.length > 1) {
      logger.warn("This selection call does not yet support multiple models.");
    }
  }

  _fireAggregateSelectionChangedEvent(mouseButton) {

    var perModel = [];

    for (var i = 0; i < this._models.length; i++) {
      var dbIdArray = [];
      var fragIdsArray = [];

      var sset = this._models[i].selector.selectedObjectIds;
      var instanceTree = this._models[i].selector.getInstanceTree();
      for (var p in sset) {
        if (sset[p]) {
          var dbId = parseInt(p);
          if (dbId) {
            dbIdArray.push(dbId);

            if (instanceTree) {
              instanceTree.enumNodeFragments(dbId, (fragId) => {
                fragIdsArray.push(fragId);
              }, false);
            }
          }
        }
      }

      if (dbIdArray.length) {
        perModel.push({
          fragIdsArray: fragIdsArray,
          dbIdArray: dbIdArray,
          nodeArray: dbIdArray,
          model: this._models[i]
        });
      }
    }

    var event;

    //For backwards compatibility, fire the old selection change event
    //when there is just one model in the scene
    if (this._models.length === 1) {
      event = {
        type: et.SELECTION_CHANGED_EVENT,
        fragIdsArray: perModel[0] ? perModel[0].fragIdsArray : [],
        dbIdArray: perModel[0] ? perModel[0].dbIdArray : [],
        nodeArray: perModel[0] ? perModel[0].dbIdArray : [],
        model: this._models[0],
        mouseButton
      };
      this._viewer.api.dispatchEvent(event);
    }

    //Always fire the aggregate selection changed event
    event = {
      type: et.AGGREGATE_SELECTION_CHANGED_EVENT,
      selections: perModel,
      mouseButton
    };
    this._viewer.api.dispatchEvent(event);

  }


  _deselectInvisible() {

    var changed = false;

    for (var i = 0; i < this._models.length; i++) {
      changed = this._models[i].selector.deselectInvisible() || changed;
    }

    if (changed)
    this._fireAggregateSelectionChangedEvent();
  }

  // @returns {boolean} true when a part is selected in any scene model.
  hasSelection() {
    for (var i = 0; i < this._models.length; i++) {
      if (this._models[i].selector.hasSelection())
      return true;
    }
    return false;
  }

  getSelectionLength() {
    var total = 0;

    for (var i = 0; i < this._models.length; i++) {
      total += this._models[i].selector.getSelectionLength();
    }

    return total;
  }

  getSelection() {
    this._warn();
    if (this._models.length > 1)
    logger.warn("Use getAggregateSelection instead of getSelection when there are multiple models in the scene.");

    return this._models[0] ? this._models[0].selector.getSelection() : [];
  }

  getAggregateSelection() {
    var res = [];
    for (var i = 0; i < this._models.length; i++) {
      var selset = this._models[i].selector.getSelection();
      if (selset && selset.length)
      res.push({ model: this._models[i], selection: selset });
    }

    return res;
  }

  /**
   * True when a hit-test result is within the current aggregate selection
   * @param {{ dbId?: number, model?: object } | null | undefined} hitResult
   * @returns {boolean}
   */
  isHitInAggregateSelection(hitResult) {
    if (!hitResult || hitResult.dbId === undefined || !hitResult.model) {
      return false;
    }
    const match = this._models.find((model) =>
    model === hitResult.model ||
    hitResult.model.id !== undefined && hitResult.model.id !== null && hitResult.model.id === model.id
    );
    return !!match && match.selector.isSelected(hitResult.dbId);
  }

  clearSelection(nofire) {
    let selectionCleared;
    for (var i = 0; i < this._models.length; i++) {
      if (this._models[i].selector.clearSelection(nofire)) {
        selectionCleared = true;
      }
    }
    if (!nofire && selectionCleared)
    this._fireAggregateSelectionChangedEvent();
    return selectionCleared;
  }

  toggleSelection(dbId, model, selectionType) {
    if (this.selectionDisabled) {
      return;
    }

    if (!model) {
      this._warn();
      model = this._models[0];
    }
    model.selector.toggleSelection(dbId, selectionType);

    this._fireAggregateSelectionChangedEvent();
  }

  setSelectionMode(mode) {
    for (var i = 0; i < this._models.length; i++)
    this._models[i].selector.setSelectionMode(mode);
  }

  isNodeSelectionLocked(dbId, model) {
    if (!model) {
      this._warn();
      model = this._models[0];
    }
    return model && model.selector.isNodeSelectionLocked(dbId);
  }

  lockSelection(nodeList, locked, model) {
    if (!model) {
      this._warn();
      model = this._models[0];
    }
    model && model.selector.lockSelection(nodeList, locked);
  }

  /**
   * Unlocks nodes in models.
   * @param {Number[]} [nodeList] - dbIds to unlock. If undefined, all model dbIds are unlocked
   * @param {Autodesk.Viewing.Model} [model] - The model associated to the model
   */
  unlockSelection(nodeList, model) {
    // Unlock all nodes for all models if both the parameters are missing
    if (!nodeList && !model) {
      this._models.forEach((model) => {
        const lockedNodes = model.selector.lockedNodes.slice();
        this.lockSelection(lockedNodes, false, model);
      });
      return;
    }
    if (!model) {
      this._warn();
      model = this._models[0];
    }
    nodeList = nodeList || model.selector.lockedNodes.slice();
    this.lockSelection(nodeList, false, model);
  }

  setSelection(dbNodeArray, model, selectionType, mouseButton) {
    if (this.selectionDisabled) {
      return;
    }

    if (!dbNodeArray || dbNodeArray.length === 0)
    this.clearSelection(true);else
    {
      if (!model) {
        this._warn();
        model = this._models[0];
      } else {
        for (var i = 0; i < this._models.length; i++)
        if (this._models[i] !== model)
        this._models[i].selector.clearSelection();
      }
      model.selector.setSelection(dbNodeArray, selectionType);
    }

    this._fireAggregateSelectionChangedEvent(mouseButton);
  }


  setAggregateSelection(selection, fireEvent = true) {

    if (this.selectionDisabled) {
      return;
    }

    if (!selection || selection.length === 0) {
      this.clearSelection(true);
    } else {
      for (var i = 0; i < selection.length; ++i) {
        var model = selection[i].model;
        var ids = selection[i].ids;
        if (!ids) {
          ids = selection[i].selection;
        }
        var selectionType = selection[i].selectionType;
        model.selector.setSelection(ids, selectionType);
      }
    }

    if (fireEvent) {
      this._fireAggregateSelectionChangedEvent();
    }
  }

  getSelectionBounds() {
    if (this._models.length == 1)
    return this._models[0].selector.getSelectionBounds();else
    {
      var bbox = new THREE.Box3();
      for (var i = 0; i < this._models.length; i++) {
        var tmp = this._models[i].selector.getSelectionBounds();
        bbox.union(tmp);
      }
      return bbox;
    }
  }

  getSelectionVisibility() {

    var res = {
      // Aggregated results
      hasVisible: false,
      hasHidden: false,
      // per model specifics
      details: []
    };
    for (var i = 0; i < this._models.length; i++) {
      var subRes = this._models[i].selector.getSelectionVisibility();
      res.hasVisible = res.hasVisible || subRes.hasVisible;
      res.hasHidden = res.hasHidden || subRes.hasHidden;
      res.details.push(subRes);
    }
    return res;
  }

  dtor() {
    for (var i = 0; i < this._models.length; i++)
    this._models[i].selector.dtor();
  }
}